import { useState } from 'react'
import { useAuthStore } from '../stores'
import { ShieldAlert, X } from 'lucide-react'
import { checkAdminAccess } from '../utils/adminApi'

interface AdminPanelProps {
  onClose: () => void
  onRequestLogin: () => void
}

function AdminPanel({ onClose, onRequestLogin }: AdminPanelProps) {
  const { user } = useAuthStore()
  const [isChecking, setIsChecking] = useState(false)
  const [serverMessage, setServerMessage] = useState('')

  const handleServerCheck = async () => {
    setIsChecking(true)
    setServerMessage('')

    try {
      const result = await checkAdminAccess()
      if (result.isAdmin) {
        setServerMessage('Server check passed. Refresh your session if this account was just promoted to admin.')
      } else {
        setServerMessage(result.error || 'This account is not marked as admin on the server.')
      }
    } catch {
      setServerMessage('Server route is unavailable. Use `vercel dev` locally or deploy the app to test backend routes.')
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6">
      <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="text-[#7cff01]" size={24} />
            <h2 className="text-2xl font-bold text-white">Admin Access</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            type="button"
            aria-label="Close admin access dialog"
          >
            <X size={24} />
          </button>
        </div>

        {!user ? (
          <>
            <div className="mb-4 rounded-lg border border-yellow-700 bg-yellow-900/20 p-3 text-sm text-yellow-200">
              This area is restricted. Sign in with your approved administrator account to continue.
            </div>

            <button
              type="button"
              onClick={onRequestLogin}
              className="w-full rounded bg-green-600 py-2 font-semibold text-white transition hover:bg-green-700"
            >
              Sign In
            </button>
          </>
        ) : (
          <>
            <div className="mb-4 rounded-lg border border-red-700 bg-red-900/20 p-3 text-sm text-red-200">
              Your current account is signed in but does not have administrator privileges.
            </div>

            <button
              type="button"
              onClick={handleServerCheck}
              disabled={isChecking}
              className="mb-3 w-full rounded bg-green-600 py-2 font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isChecking ? 'Checking Server Access...' : 'Check Server Access'}
            </button>

            {serverMessage && (
              <div className="mb-3 rounded-lg border border-gray-700 bg-gray-800 p-3 text-sm text-gray-200">
                {serverMessage}
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              className="w-full rounded bg-gray-800 py-2 font-semibold text-white transition hover:bg-gray-700"
            >
              Return
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default AdminPanel
